import apiClient from './apiClient';

// Interface for user application data
export interface UserApplication {
  application_id: number;
  collab_id: number;
  brand_name: string;
  collab_name: string;
  application_date: string;
  status: string;
  referrer_username: string | null;
}

// Interface for referral success response
export interface ReferralResponse {
  success: boolean;
  message: string;
  data?: {
    application_id: number;
    collab_id: number;
    referrer_username: string;
    action_taken: string;
  };
}

/**
 * Fetch all applications submitted by the current user
 * Server endpoint format: /core/user/applications/
 */
export const getUserApplications = async (): Promise<any> => {
  try {
    // apiClient already has http://localhost:8000/api as baseURL
    const response = await apiClient.get('/core/user/applications/');
    
    // Return the entire data object which contains applications and pagination
    return response.data.data || { applications: [], pagination: {} };
  } catch (error) {
    console.error('Error fetching user applications:', error);
    throw error;
  }
};

/**
 * Add or update a referral for an existing application
 * Server endpoint format: /core/collabs/referral/
 */
export const updateReferral = async (
  applicationId: number,
  referrerUsername: string,
  action: 'add' | 'remove' = 'add'
): Promise<ReferralResponse> => {
  try {
    const payload: Record<string, any> = {
      application_id: applicationId,
      action
    };
    
    // Only include referrer_username if adding a referral
    if (action === 'add') {
      payload.referrer_username = referrerUsername;
    }
    
    // apiClient already has http://localhost:8000/api as baseURL
    const response = await apiClient.post('/core/collabs/referral/', payload);
    return response.data;
  } catch (error) {
    console.error('Error updating referral:', error);
    throw error;
  }
};

/**
 * Add a referral for a new collab application
 * Server endpoint format: /core/collabs/referral/
 */
export const addReferralWithApplication = async (
  collabId: number,
  referrerUsername: string,
  text: string,
  mediaFiles?: any[]
): Promise<ReferralResponse> => {
  try {
    const payload: Record<string, any> = {
      collab_id: collabId,
      referrer_username: referrerUsername,
      text
    };
    
    if (mediaFiles && mediaFiles.length > 0) {
      payload.media_files = mediaFiles;
    }
    
    // apiClient already has http://localhost:8000/api as baseURL
    const response = await apiClient.post('/core/collabs/referral/', payload);
    return response.data;
  } catch (error) {
    console.error('Error adding referral with application:', error);
    throw error;
  }
};

/**
 * Get all referrals made by the current user
 * Server endpoint format: /core/user/referrals/
 */
export const getUserReferrals = async (
  page: number = 1,
  pageSize: number = 10
): Promise<any> => {
  try {
    // apiClient already has http://localhost:8000/api as baseURL
    const response = await apiClient.get('/core/user/referrals/', {
      params: {
        page,
        page_size: pageSize
      }
    });
    return response.data.data;
  } catch (error) {
    console.error('Error fetching user referrals:', error);
    throw error;
  }
};

/**
 * Get all referrals for a specific collab
 * Server endpoint format: /core/collabs/{collab_id}/referrals/
 */
export const getCollabReferrals = async (
  collabId: number,
  page: number = 1,
  pageSize: number = 10
): Promise<any> => {
  try {
    // apiClient already has http://localhost:8000/api as baseURL
    const response = await apiClient.get(`/core/collabs/${collabId}/referrals/`, {
      params: {
        page,
        page_size: pageSize
      }
    });
    return response.data.data;
  } catch (error) {
    console.error('Error fetching collab referrals:', error);
    throw error;
  }
};

/**
 * Get autocomplete suggestions for referrer usernames
 * Server endpoint format: /core/referrer/autocomplete
 */
export interface ReferrerSuggestion {
  user_id: number;
  username: string;
  tiktok_username: string | null;
  profile_picture: string | null;
}

export const getReferrerSuggestions = async (query: string, limit: number = 10): Promise<ReferrerSuggestion[]> => {
  try {
    // apiClient already has http://localhost:8000/api as baseURL
    const response = await apiClient.get('/core/referrer/autocomplete', {
      params: {
        q: query,
        limit
      }
    });
    return response.data.data || [];
  } catch (error) {
    console.error('Error fetching referrer suggestions:', error);
    return [];
  }
}; 